import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";

import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Search,
  Download,
  MoreVertical,
  Calendar,
  Phone,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { codeService } from "@/services/code";
import { DateUtils } from "@/utils/dateUtils";
import {
  AddPartnerParams,
  Code,
  CreateCodesParams,
  GetCodesParams,
  Partner,
  TIME_OPTIONS,
} from "@/types/code";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { Label } from "@/components/ui/label";

export default function CodeManagement() {
  // 分页
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();
  // 筛选项列表
  const [platformList, setPlatformList] = useState<
    { id: string; label: string }[]
  >([]);
  const [cardTypeList, setCardTypeList] = useState<
    { id: string; label: string }[]
  >([]);
  const [trialCardList, setTrialCardList] = useState<
    { id: string; label: string; system_dict_id: string }[]
  >([]);
  const [officialCardList, setOfficialCardList] = useState<
    { id: string; label: string; system_dict_id: string }[]
  >([]);
  const [giftList, setGiftList] = useState<
    { id: string; label: string; system_dict_id: string }[]
  >([]);

  // 数据
  const [loading, setLoading] = useState(true);
  // 保存原始数据
  const [codes, setCodes] = useState<Code[]>([]);

  // 选中的数据
  const [selectedCode, setSelectedCode] = useState<Code | null>();

  // 1) 合作伙伴筛选
  const [platformSearch, setPlatformSearch] = useState<string>("");
  const [partnerSearch, setPartnerSearch] = useState<string>("");
  const [partnerList, setPartnerList] = useState<Partner[]>();

  // 2) 兑换码名称筛选
  const [codeName, setCodeName] = useState<string>("");

  // 3) 条件查询-时间筛选
  const [time, setTime] = useState<
    "created_at" | "valid_time" | "invalid_time" | ""
  >("");
  const [timeStart, setTimeStart] = useState<string>();
  const [timeEnd, setTimeEnd] = useState<string>();

  // 4) 卡类型筛选
  const [cardTypeSearch, setCardTypeSearch] = useState("0");
  const [cardDurationSearch, setCardDurationSearch] = useState("0");

  // 5) 赠品筛选
  const [gift, setGift] = useState<"0" | "1" | "-1">("-1");
  // 6) 状态筛选
  // const [status, setStatus] = useState<string>("all");

  // 修改赠品状态的初始值;
  const [giftInForm, setGiftInForm] = useState<string>("");

  // 创建兑换码弹窗相关状态
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createCodeParams, setCreateCodeParams] =
    useState<CreateCodesParams | null>();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [createStartTime, setCreateStartTime] = useState<string | undefined>();
  const [createEndTime, setCreateEndTime] = useState<string | undefined>();
  const [createCardType, setCreateCardType] = useState<string>("4");
  const [createChosenPlatform, setCreateChosenPlatform] = useState<string>("");

  // 导出弹窗相关状态
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [processingExport, setProcessingExport] = useState(false);

  // 新增合作伙伴弹窗相关
  const [addPartnerDialogOpen, setAddPartnerDialogOpen] = useState(false);
  const [addPartnerParams, setAddPartnerParams] = useState<AddPartnerParams>();
  const addPartnerFormRef = useRef<HTMLFormElement>(null);

  // 设置生效、设置失效弹窗相关状态
  const [invalidateDialogOpen, setInvalidateDialogOpen] = useState(false);
  const invalidFormRef = useRef<HTMLFormElement>(null);
  const [invalidReason, setInvalidReason] = useState("");

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  // UI状态 -> 接口参数映射
  const buildRequestParams = () => {
    const params: GetCodesParams = {
      page,
      page_size: pageSize,
    };

    // 平台筛选
    if (platformSearch) {
      params.platform_id = Number(platformSearch);
    }
    // 合作伙伴筛选
    if (partnerSearch) {
      params.partner_id = Number(partnerSearch);
    }
    // 兑换码
    if (codeName) {
      params.name = codeName;
    }
    // 时间筛选
    if (time) {
      params.time_field = time;
    }
    // 兑换码时间筛选
    if (timeStart) {
      params.start_time = DateUtils.toChineseISOString(timeStart, false);
    }
    if (timeEnd) {
      params.end_time = DateUtils.toChineseISOString(timeEnd, true);
    }
    // 卡类型筛选
    if (cardTypeSearch !== "0") {
      params.card_type = Number(cardTypeSearch);
    }
    // 时长筛选
    if (cardDurationSearch !== "0") {
      params.card_spec = cardDurationSearch;
    }
    // 赠品筛选
    if (gift !== "-1") {
      params.gift = gift;
    }
    return params;
  };

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const params = buildRequestParams();
      const res = (await codeService.getCodes(params)) as {
        list?: Code[];
        total?: number;
      };

      // 更新映射逻辑以匹配API返回的结构
      const list = res.list ?? [];
      const totalCount = res.total ?? 0;
      setCodes(list);
      setTotal(totalCount);
    } catch (e) {
      console.error(e);
      toast.error("获取兑换码列表失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchCodes();
  };
  const handleCreate = () => {
    setCreateDialogOpen(true);
    setCreateCodeParams({} as CreateCodesParams);
  };

  const resetFilters = () => {
    setTimeStart("");
    setTimeEnd("");
    setPlatformSearch("");
    setPartnerSearch("");
    setCodeName("");
    setCardTypeSearch("0");
    setCardDurationSearch("0");
    setTime("");
    setGift("-1");
    setPage(1);
  };

  // 打开导出确认弹窗
  const handleExport = () => {
    setExportDialogOpen(true);
  };

  // 执行实际导出操作
  const confirmExport = async () => {
    setProcessingExport(true);
    try {
      // 构建请求参数，与 getInvitations 使用相同的参数结构
      const params = buildRequestParams();

      // 调用 inviteService 的 exportInvitations 方法
      const blob = await codeService.downloadCodesBatch(params);

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `兑换码批次记录_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(a);
      a.click();

      // 清理
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("导出成功", {
        description: "兑换码批次记录已成功导出",
      });

      // 关闭弹窗
      setExportDialogOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("导出失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setProcessingExport(false);
    }
  };

  const formRef = useRef<HTMLFormElement>(null);
  // 创建兑换码弹窗
  const onCreateDialogChange = (open: boolean) => {
    if (!open) {
      setCreateDialogOpen(false);
      setCreateStartTime(undefined);
      setCreateEndTime(undefined);
      setCreateChosenPlatform("");
      setCreateCardType("4");
      setGiftInForm(""); // 重置赠品选择
    }
  };
  const fetchPartners = async (from: "search" | "create" = "create") => {
    // setLoading(true);
    try {
      const res = (await codeService.getPartners({
        page: 1,
        page_size: 99,
        platform_id:
          from === "create"
            ? createCodeParams?.platform_id
            : Number(platformSearch),
      })) as { list?: Partner[]; total?: number };
      // 更新映射逻辑以匹配API返回的结构
      const list = res.list ?? [];
      if (from === "create") {
        setPartners(list);
      } else {
        setPartnerList(list);
      }
    } catch (e) {
      console.error(e);
      toast.error("获取合作伙伴列表失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      // setLoading(false);
    }
  };
  // 创建兑换码表单状态变更
  const onCreateFormChange = (
    key:
      | "card_spec_id"
      | "card_type_id"
      | "end_time"
      | "gift_id"
      | "name"
      | "partner_id"
      | "platform_id"
      | "remark"
      | "start_time"
      | "total_quantity",
    value: string
  ) => {
    if (key === "start_time") {
      setCreateStartTime(value);
      value = value + "T00:00:00.000+08:00";
    }
    if (key === "end_time") {
      setCreateEndTime(value);
      value = value + "T23:59:59.000+08:00";
    }
    if (key == "platform_id") {
      setCreateChosenPlatform(value);
    }
    setCreateCodeParams((state: CreateCodesParams | null | undefined) => {
      if (state) {
        (state as { [key: string]: string | number })[key] = key.includes("id")
          ? Number(value)
          : value;
      }
      return state;
    });
  };
  // 创建兑换码表单提交函数

  // 4. 修改表单提交函数,处理无赠品的情况
  const onCreateCodeFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!createCodeParams) return;

    // 构建提交参数
    const submitParams: any = {
      ...createCodeParams,
      card_spec_id: Number(createCodeParams.card_spec_id),
      card_type_id: Number(createCodeParams.card_type_id),
      partner_id: Number(createCodeParams.partner_id),
      platform_id: Number(createCodeParams.platform_id),
      total_quantity: Number(createCodeParams.total_quantity),
    };

    // 只有选择了赠品(不是"0")时才添加gift_id
    if (giftInForm !== "0" && createCodeParams.gift_id) {
      submitParams.gift_id = Number(createCodeParams.gift_id);
    }

    try {
      await codeService.createCodes(submitParams);
      await fetchCodes();
      toast.success("创建成功");
    } catch (err) {
      console.error(err);
      toast.error("操作失败", {
        description: err instanceof Error ? err.message : "请检查表单内容",
      });
    } finally {
      setCreateDialogOpen(false);
    }
  };

  // 新增合作伙伴表单状态监控函数
  const onAddPartnerFormChange = (
    key: "name" | "platform_id" | "remark",
    value: string
  ) => {
    setAddPartnerParams((state) => {
      if (state) {
        state[key] = value;
      }
      return state;
    });
  };
  // 新增合作伙伴表单提交函数
  const onAddPartnerFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!addPartnerParams) {
      return;
    }
    if (!addPartnerParams.platform_id || !addPartnerParams.name) {
      toast.warning("请完善表单");
      return;
    }
    try {
      await codeService.addPartner(addPartnerParams);
      toast.success("合作伙伴添加成功");
      setAddPartnerDialogOpen(false);
      fetchPartners();
    } catch (err) {
      console.error(err);
      toast.error("添加失败", {
        description: err instanceof Error ? err.message : "请检查表单内容",
      });
    }
  };

  // 跳转兑换码详情列表
  const gotoCodeDetail = (code: Code) => {
    navigate({
      pathname: "/codes/detail",
      search: `?id=${code.id}`,
    });
  };
  // 兑换码下载
  const downloadCode = async (code: Code) => {
    try {
      const blob = await codeService.downloadCodes({
        code_id: Number(code.id),
        page: 1,
        page_size: 999,
      });
      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `批次${code.id}兑换码记录_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(a);
      a.click();

      // 清理
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("下载成功", {
        description: `批次${code.id}兑换码记录已成功下载`,
      });
    } catch (e) {
      console.error(e);
      toast.error("下载失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setProcessingExport(false);
    }
  };

  // 设置失效/生效弹窗
  const setInvalidateDialogOpenChange = (open: boolean) => {
    if (!open) {
      setInvalidateDialogOpen(false);
      setSelectedCode(null);
      setInvalidReason("");
    }
  };
  const onSetInvalidFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedCode?.id) return;
    try {
      if (selectedCode.Status === "invalidated") {
        // 设置生效
        await codeService.setValid({
          id: selectedCode?.id,
          reason: invalidReason,
        });
      } else {
        // 设置失效
        await codeService.setInvalid({
          id: selectedCode?.id,
          reason: invalidReason,
        });
      }
      await fetchCodes();
      setInvalidateDialogOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error("设置失败", {
        description: err instanceof Error ? err.message : "请检查表单内容",
      });
    }
  };
  const getOptions = async () => {
    try {
      const res = await codeService.getOptionsList(
        "platform,time_field,card,trialCard,officialCard,gifts"
      );
      setPlatformList(res.platform);
      setCardTypeList(res.card);
      setTrialCardList(res.trialCard);
      setOfficialCardList(res.officialCard);
      setGiftList(res.gifts.reverse());
    } catch (e) {
      console.error(e);
      toast.error("获取筛选项列表失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    }
  };
  useEffect(() => {
    getOptions();
  }, []);
  useEffect(() => {
    fetchCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);
  // 兑换码创建-切换平台，合作伙伴数据源切换
  useEffect(() => {
    fetchPartners();
  }, [createChosenPlatform]);
  // 筛选-切换平台，合作伙伴数据源切换
  useEffect(() => {
    fetchPartners("search");
  }, [platformSearch]);

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">兑换码管理</h2>
      </div>
      {/* 筛选区 - 现在分为两行 */}
      <div className="rounded-lg border p-4 space-y-4">
        {/* 第一行 */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* 1. 合作伙伴 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">合作伙伴</label>
            <div className="flex gap-2">
              <Select
                value={platformSearch}
                onValueChange={(v) => {
                  setPlatformSearch(v);
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="选择平台" />
                </SelectTrigger>
                <SelectContent>
                  {platformList?.map(({ label, id }) => {
                    return (
                      <SelectItem key={id} value={id}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Select
                value={partnerSearch}
                onValueChange={(v) => setPartnerSearch(v)}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="选择合作伙伴" />
                </SelectTrigger>
                <SelectContent>
                  {partnerList?.length
                    ? partnerList.map((item) => {
                        return (
                          <SelectItem key={item.id} value={item.id}>
                            {item.Name}
                          </SelectItem>
                        );
                      })
                    : "暂无合作伙伴"}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* 2. 兑换码名称 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">兑换码名称</label>
            <div className="flex gap-3">
              <Input
                value={codeName}
                onChange={(e) => setCodeName(e.target.value)}
                placeholder="请输入兑换码名称"
              />
            </div>
          </div>
          {/* 3. 时间 */}
          <div className="flex flex-col gap-2 col-span-2">
            <label className="text-sm text-gray-600">时间</label>
            <div className="flex gap-3">
              <Select
                value={time}
                onValueChange={(
                  v: "created_at" | "valid_time" | "invalid_time"
                ) => setTime(v)}
              >
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="选择字段" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_OPTIONS.map(({ key, value }) => {
                    return (
                      <SelectItem key={key} value={key}>
                        {value}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={timeStart}
                onChange={(e) => setTimeStart(e.target.value)}
              />
              <Input
                type="date"
                value={timeEnd}
                onChange={(e) => setTimeEnd(e.target.value)}
              />
            </div>
          </div>
        </div>
        {/* 第二行 */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* 4. 卡类型 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">卡类型</label>
            <div className="flex gap-2">
              <Select
                value={cardTypeSearch}
                onValueChange={(v) => setCardTypeSearch(v)}
              >
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="选择卡类型" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0" key="0">
                    不限
                  </SelectItem>
                  {cardTypeList.map(({ label, id }) => {
                    return (
                      <SelectItem key={id} value={id}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Select
                value={cardDurationSearch}
                onValueChange={(v) => setCardDurationSearch(v)}
              >
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="选择会员时长" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0" key="0">
                    不限
                  </SelectItem>
                  {[...officialCardList, ...trialCardList]
                    .filter(({ system_dict_id }) => {
                      if (
                        cardTypeSearch == undefined ||
                        cardTypeSearch == "0"
                      ) {
                        return true;
                      }
                      return system_dict_id == cardTypeSearch;
                    })
                    .map(({ id, label }) => {
                      return (
                        <SelectItem key={id} value={id}>
                          {label}
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 5. 赠品*/}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">赠品</label>
            <div className="flex gap-2">
              <Select
                value={gift}
                onValueChange={(v: "0" | "1" | "-1") => setGift(v)}
              >
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="选择赠品" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="-1">不限</SelectItem>
                  {giftList.map((gift) => {
                    return (
                      <SelectItem key={gift.id} value={String(gift.id)}>
                        {gift.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* 6. 操作按钮组 */}
          <div className="flex flex-col gap-2 justify-end column-2 col-end-5">
            <label className="text-sm text-gray-600 opacity-0">操作</label>
            <div className="flex gap-2 justify-end">
              <Button className="w-24" variant="outline" onClick={resetFilters}>
                重置
              </Button>
              <Button className="w-24" onClick={handleSearch}>
                <Search className="mr-2 h-4 w-4" />
                查询
              </Button>
              <Button
                className="w-24"
                variant="secondary"
                onClick={handleExport}
              >
                <Download className="mr-2 h-4 w-4" />
                导出
              </Button>
              <Button className="w-24" onClick={handleCreate}>
                创建
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* 列表：可滚动容器 */}
      <div className="rounded-md border overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">id</TableHead>
              <TableHead className="whitespace-nowrap">平台</TableHead>
              <TableHead className="whitespace-nowrap">合作伙伴</TableHead>
              <TableHead className="whitespace-nowrap">兑换码名称</TableHead>
              <TableHead className="whitespace-nowrap">卡类型</TableHead>
              <TableHead className="whitespace-nowrap">卡规格</TableHead>
              <TableHead className="whitespace-nowrap">赠品</TableHead>
              <TableHead className="whitespace-nowrap">兑换码数量</TableHead>
              <TableHead className="whitespace-nowrap">可兑换时间</TableHead>
              <TableHead className="whitespace-nowrap">已激活</TableHead>
              <TableHead className="whitespace-nowrap">已核销</TableHead>
              <TableHead className="whitespace-nowrap">未激活</TableHead>
              <TableHead className="whitespace-nowrap">退款</TableHead>
              <TableHead className="whitespace-nowrap">已过期</TableHead>
              <TableHead className="whitespace-nowrap">已设置失效</TableHead>
              <TableHead className="whitespace-nowrap">生成时间</TableHead>
              <TableHead className="whitespace-nowrap">备注</TableHead>
              <TableHead className="whitespace-nowrap">生效/失效理由</TableHead>
              <TableHead className="whitespace-nowrap">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={19} className="h-24 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : codes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={19} className="h-24 text-center">
                  没有找到兑换码记录
                </TableCell>
              </TableRow>
            ) : (
              codes.map((code) => (
                <TableRow key={code.id}>
                  <TableCell className="whitespace-nowrap">
                    {code.id || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.PlatformID
                      ? platformList.filter(
                          (item) => Number(item.id) == code.PlatformID
                        )[0]?.label
                      : "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code?.Partner?.Name || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.Name || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.CardTypeID
                      ? cardTypeList.filter(
                          (item) => Number(item.id) == code.CardTypeID
                        )[0]?.label
                      : "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.CardSpecID
                      ? [...officialCardList, ...trialCardList].filter(
                          (item) => Number(item.id) == code.CardSpecID
                        )[0]?.label
                      : "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {/* {code.Gift || "-"} */}
                    {code.GiftID
                      ? giftList.filter(
                          (item) => item.id == String(code.GiftID)
                        )?.[0]?.label
                      : "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.TotalQuantity || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {`${DateUtils.formatDate(
                      new Date(code.StartTime)
                    )}-${DateUtils.formatDate(new Date(code.EndTime))}`}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.ActivatedCount || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.UsedCount || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.InactiveCount || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.RefundCount || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.ExpiredCount || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.InvalidatedCount || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {DateUtils.formatDateTime(new Date(code.created_at))}
                  </TableCell>
                  <TableCell
                    className="max-w-[150px] truncate"
                    title={code.Remark}
                    // className="whitespace-nowrap"
                  >
                    {code.Remark || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {code.InvalidReason || "-"}
                  </TableCell>

                  <TableCell className="whitespace-nowrap flex items-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setInvalidateDialogOpen(true);
                              setSelectedCode(code);
                            }}
                            className="w-auto"
                          >
                            {code.Status === "expired" ? (
                              "-"
                            ) : code.Status !== "active" ? (
                              <p className="h-4 pr-4 text-red-500">
                                已设置失效
                              </p>
                            ) : (
                              <p className="h-4 pr-4 text-green-500">
                                设置失效
                              </p>
                            )}
                          </Button>
                        </TooltipTrigger>
                      </Tooltip>
                    </TooltipProvider>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-muted/60 rounded-full"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        sideOffset={4}
                        className="w-40 rounded-xl shadow-lg border bg-white"
                      >
                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                          相关操作
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => gotoCodeDetail(code)}
                          className="cursor-pointer"
                        >
                          <Calendar className="mr-2 h-4 w-4 text-emerald-600" />
                          详情
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => downloadCode(code)}
                          className="cursor-pointer"
                        >
                          <Phone className="mr-2 h-4 w-4 text-indigo-600" />
                          下载
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {/* 导出确认弹窗 */}
      <Dialog
        open={exportDialogOpen}
        onOpenChange={(open) => !open && setExportDialogOpen(false)}
        key="export"
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>确认导出兑换码记录</DialogTitle>
            <DialogDescription>
              将当前筛选条件下的兑换码记录导出为Excel文件
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">导出记录数量:</span>
                <span className="font-medium text-blue-600">{total}</span>
              </div>
              {platformSearch && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">平台:</span>
                  <span className="text-gray-600">
                    {
                      platformList.filter(({ id }) => id == platformSearch)[0]
                        .label
                    }
                  </span>
                </div>
              )}
              {partnerSearch && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">合作伙伴:</span>
                  <span className="text-gray-600">
                    {
                      partnerList?.filter(
                        (item) => item.id === partnerSearch
                      )[0].Name
                    }
                  </span>
                </div>
              )}
              {codeName && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">兑换码:</span>
                  <span className="text-gray-600">{codeName}</span>
                </div>
              )}
              {time && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">时间类型</span>
                  <span className="text-gray-600">
                    {TIME_OPTIONS.filter((item) => item.key === time)[0].value}
                  </span>
                </div>
              )}
              {time && timeStart && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {TIME_OPTIONS.filter((item) => item.key === time)[0].value}
                    开始时间:
                  </span>
                  <span className="text-gray-600">{timeStart}</span>
                </div>
              )}
              {time && timeEnd && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {TIME_OPTIONS.filter((item) => item.key === time)[0].value}
                    结束时间:
                  </span>
                  <span className="text-gray-600">{timeEnd}</span>
                </div>
              )}
              {cardTypeSearch !== "0" && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">卡类型:</span>
                  <span className="text-gray-600">
                    {
                      cardTypeList.filter(
                        (item) => item.id == cardTypeSearch
                      )[0]?.label
                    }
                  </span>
                </div>
              )}
              {cardDurationSearch !== "0" && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">会员时长:</span>
                  <span className="text-gray-600">
                    {
                      [...officialCardList, ...trialCardList].filter(
                        (item) => item.id == cardDurationSearch
                      )[0]?.label
                    }
                  </span>
                </div>
              )}
              {gift !== "-1" && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">赠品:</span>
                  <span className="text-gray-600">
                    {gift == "0" ? "无赠品" : "有赠品"}
                  </span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500">
              导出的Excel文件将包含所有当前筛选条件下的兑换码记录数据
            </p>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button onClick={confirmExport} disabled={processingExport}>
              {processingExport ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
                  导出中...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  确认导出
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {!loading && codes.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 whitespace-nowrap">
            共 {total} 条兑换码记录，第 {page} 页 / 共 {totalPages} 页
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => page > 1 && setPage(page - 1)}
                  className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <PaginationItem key={i}>
                    <PaginationLink
                      isActive={page === pageNum}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => page < totalPages && setPage(page + 1)}
                  className={
                    page >= totalPages ? "pointer-events-none opacity-50" : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
      {/* 创建兑换码弹窗 */}
      {
        <Dialog
          open={createDialogOpen}
          onOpenChange={onCreateDialogChange}
          key="new-code"
        >
          <DialogContent className="sm:max-w-3xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-center">创建兑换码</DialogTitle>
            </DialogHeader>
            <form
              ref={formRef}
              onSubmit={onCreateCodeFormSubmit}
              className="max-h-[70vh] overflow-y-auto p-1 pr-4 space-y-6"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="name" className="text-right">
                    兑换码名称<span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    className="col-span-5"
                    placeholder="名称会展示在兑换码中"
                    required
                    autoComplete="off"
                    onChange={(e) => onCreateFormChange("name", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="platform_id" className="text-right">
                    平台<span className="text-red-500">*</span>
                  </Label>
                  <Select
                    onValueChange={(val: string) =>
                      onCreateFormChange("platform_id", val)
                    }
                    required
                  >
                    <SelectTrigger className="col-span-5">
                      <SelectValue id="platform_id" placeholder="选择平台" />
                    </SelectTrigger>
                    <SelectContent>
                      {platformList.map(({ label, id }) => {
                        return (
                          <SelectItem key={id} value={id}>
                            {label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="partner_id" className="text-right">
                    合作伙伴<span className="text-red-500">*</span>
                  </Label>
                  <Select
                    onValueChange={(val: string) =>
                      onCreateFormChange("partner_id", val)
                    }
                    required
                    disabled={createChosenPlatform === ""}
                  >
                    <SelectTrigger className="col-span-4">
                      <SelectValue id="partner_id" placeholder={createChosenPlatform=== ""?"请先选择平台":"选择合作伙伴"} />
                      {/* <div className="flex">备注:{}有30个3-6岁的妈妈群，购买力中等</div> */}
                    </SelectTrigger>
                    <SelectContent>
                      {partners.map((item) => {
                        return (
                          <SelectItem key={item.id} value={String(item.id)}>
                            {item.Name}
                          </SelectItem>
                        );
                      })}
                      {partners.length === 0 && (
                        <div className="flex justify-center">暂无合作伙伴</div>
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => {
                      setAddPartnerDialogOpen(true);
                      setAddPartnerParams({} as AddPartnerParams);
                    }}
                  >
                    新增
                  </Button>
                </div>
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label className="text-right" htmlFor="card_type_id">
                    卡类型<span className="text-red-500">*</span>
                  </Label>
                  {cardTypeList.map(
                    ({ id, label }: { id: string; label: string }) => {
                      return (
                        <div
                          className="flex items-center gap-1 text-sm"
                          key={id}
                        >
                          <input
                            required
                            type="radio"
                            name="card_type_id"
                            value={id}
                            id={id}
                            onChange={(e) => {
                              if (e.target.checked) {
                                onCreateFormChange(
                                  "card_type_id",
                                  e.target.value
                                );
                                setCreateCardType(e.target.value);
                              }
                            }}
                          />
                          <label className="Label" htmlFor={id}>
                            {label}
                          </label>
                        </div>
                      );
                    }
                  )}
                </div>
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="card_spec_id" className="text-right">
                    卡规格<span className="text-red-500">*</span>
                  </Label>
                  {createCardType == "4"
                    ? officialCardList.map(({ id, label }) => {
                        return (
                          <div
                            className="flex items-center gap-1 text-sm"
                            key={id}
                          >
                            <input
                              required
                              type="radio"
                              name="card_spec_id"
                              value={id}
                              id={id}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  onCreateFormChange(
                                    "card_spec_id",
                                    e.target.value
                                  );
                                }
                              }}
                            />
                            <label className="Label" htmlFor={id}>
                              {label}
                            </label>
                          </div>
                        );
                      })
                    : trialCardList.map(({ id, label }) => {
                        return (
                          <div
                            className="flex items-center gap-1 text-sm"
                            key={id}
                          >
                            <input
                              required
                              type="radio"
                              name="card_spec_id"
                              value={id}
                              id={id}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  onCreateFormChange(
                                    "card_spec_id",
                                    e.target.value
                                  );
                                }
                              }}
                            />
                            <label className="Label" htmlFor={id}>
                              {label}
                            </label>
                          </div>
                        );
                      })}
                </div>
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="total_quantity" className="text-right">
                    兑换码数量<span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="total_quantity"
                    className="col-span-5"
                    placeholder="请输入兑换码数量"
                    required
                    autoComplete="off"
                    type="number"
                    onChange={(e) =>
                      onCreateFormChange("total_quantity", e.target.value)
                    }
                  />
                </div>
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="gift" className="text-right">
                    赠品<span className="text-red-500">*</span>
                  </Label>
                  <div className="col-span-5">
                    <Select
                      value={giftInForm}
                      onValueChange={(val: string) => {
                        setGiftInForm(val);
                        if (val !== "0") {
                          onCreateFormChange("gift_id", val);
                        }
                      }}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue id="gift" placeholder="选择赠品" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">无赠品</SelectItem>
                        {giftList.map((gift) => {
                          return (
                            <SelectItem key={gift.id} value={String(gift.id)}>
                              {gift.label}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="time" className="text-right">
                    生效时间<span className="text-red-500">*</span>
                  </Label>
                  <div className="col-span-5 flex items-center gap-2">
                    <div className="relative w-full">
                      <Input
                        id="start_time"
                        required
                        type={createCodeParams?.start_time ? "date" : "text"}
                        placeholder="请选择开始日期"
                        onFocus={(e) => (e.target.type = "date")}
                        onBlur={(e) => {
                          if (!e.target.value) {
                            e.target.type = "text";
                          }
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          onCreateFormChange("start_time", value || "");
                        }}
                      />
                      {createStartTime && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-gray-400 hover:text-gray-700"
                          onClick={() => onCreateFormChange("start_time", "")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <span className="text-gray-500">-</span>
                    <div className="relative w-full">
                      <Input
                        type={createCodeParams?.end_time ? "date" : "text"}
                        required
                        placeholder="请选择结束日期"
                        onFocus={(e) => (e.target.type = "date")}
                        onBlur={(e) => {
                          if (!e.target.value) {
                            e.target.type = "text";
                          }
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          onCreateFormChange("end_time", value || "");
                        }}
                      />
                      {createEndTime && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-gray-400 hover:text-gray-700"
                          onClick={() => onCreateFormChange("end_time", "")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="remark" className="text-right">
                    备注
                  </Label>
                  <Textarea
                    id="remark"
                    className="col-span-5"
                    onChange={(e) =>
                      onCreateFormChange("remark", e.target.value)
                    }
                  />
                </div>
              </div>
            </form>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">取消</Button>
              </DialogClose>
              <Button onClick={() => formRef.current?.requestSubmit()}>
                确认
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
      {/* 新增合作伙伴弹窗 */}
      {
        <Dialog
          open={addPartnerDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              setAddPartnerDialogOpen(false);
            }
          }}
          key="add-partner"
        >
          <DialogContent className="sm:max-w-3xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-center">新增合作伙伴</DialogTitle>
            </DialogHeader>
            <form
              ref={addPartnerFormRef}
              onSubmit={onAddPartnerFormSubmit}
              className="max-h-[70vh] overflow-y-auto p-1 pr-4 space-y-6"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="platform_id" className="text-right">
                    平台/渠道<span className="text-red-500">*</span>
                  </Label>
                  <Select
                    onValueChange={(val: string) =>
                      onAddPartnerFormChange("platform_id", val)
                    }
                    required
                  >
                    <SelectTrigger className="col-span-5">
                      <SelectValue id="platform_id" placeholder="选择平台" />
                    </SelectTrigger>
                    <SelectContent>
                      {platformList.map(({ label, id }) => {
                        return (
                          <SelectItem key={id} value={id}>
                            {label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="name" className="text-right">
                    合作伙伴名称<span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    className="col-span-5"
                    required
                    onChange={(e) =>
                      onAddPartnerFormChange("name", e.target.value)
                    }
                  />
                </div>
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="remark" className="text-right">
                    备注
                  </Label>
                  <Textarea
                    id="remark"
                    className="col-span-5"
                    onChange={(e) =>
                      onAddPartnerFormChange("remark", e.target.value)
                    }
                  />
                </div>
              </div>
            </form>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">取消</Button>
              </DialogClose>
              <Button
                onClick={() => addPartnerFormRef.current?.requestSubmit()}
              >
                创建
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
      {/* 设置失效/生效弹窗 */}
      <Dialog
        open={invalidateDialogOpen}
        onOpenChange={setInvalidateDialogOpenChange}
        key="invalid"
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-center">
              {selectedCode?.Status === "active" ? "设置失效" : "设置生效"}
            </DialogTitle>
          </DialogHeader>
          <form
            ref={invalidFormRef}
            onSubmit={onSetInvalidFormSubmit}
            className="max-h-[70vh] overflow-y-auto p-1 pr-4 space-y-6"
          >
            <div className="space-y-4">
              <div className="grid grid-cols-6 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  兑换码批次
                </Label>
                {selectedCode?.id}
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-6 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  {selectedCode?.Status === "active" ? "失效" : "生效"}原因
                  <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="remark"
                  className="col-span-5"
                  required
                  value={invalidReason}
                  placeholder={`请填写设置${
                    selectedCode?.Status === "active" ? "失效" : "生效"
                  }原因`}
                  onChange={(e) => setInvalidReason(e.target.value)}
                />
              </div>
            </div>
          </form>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button
              disabled={!invalidReason}
              onClick={() => invalidFormRef.current?.requestSubmit()}
            >
              确认
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
